// Catalog of panel widgets on the main dashboard and the one rule for which of
// them a user may pin. The backend already redacts finance data per role
// (reportService.getDashboard), so `requiresFullKpis` only decides what is
// offered, never what is safe to show. Positions are in grid units: the panel
// is a 6-column grid (react-grid-layout), like a desktop widget board.

export const DASHBOARD_GRID_COLUMNS = 6;

export const DASHBOARD_WIDGET_IDS = [
  'kpi',
  'payments',
  'finance',
  'priorities',
  'week',
  'status',
  'risks',
  'recent',
  'workQueue',
  'upcoming',
  'statusChart',
  'quickActions',
  'upcomingTransports',
  'expectedDeliveries',
  'myTasks',
] as const;

export type DashboardWidgetId = typeof DASHBOARD_WIDGET_IDS[number];

// Access beyond full KPIs that a widget may need. Each mirrors the rule of the
// endpoint the widget reads (see Dashboard.tsx), so a widget is offered only
// where its data can load.
export type DashboardCapability = 'transports' | 'supplierOrders' | 'tasks';

export interface DashboardAccess {
  fullKpis: boolean;
  transports: boolean;
  supplierOrders: boolean;
  tasks: boolean;
}

export interface DashboardWidgetDefinition {
  id: DashboardWidgetId;
  defaultW: number;
  defaultH: number;
  minW: number;
  maxW: number;
  minH: number;
  requiresFullKpis: boolean;
  requires?: DashboardCapability;
  labelKey: string;
  descriptionKey: string;
}

export interface DashboardWidgetLayoutItem {
  id: DashboardWidgetId;
  x: number;
  y: number;
  w: number;
  h: number;
}

const COLS = DASHBOARD_GRID_COLUMNS;

// Keyed by id so a missing entry is a compile error, not a silent gap.
const DEFINITIONS: Record<DashboardWidgetId, Omit<DashboardWidgetDefinition, 'id'>> = {
  kpi: { defaultW: 6, defaultH: 4, minW: COLS, maxW: COLS, minH: 4, requiresFullKpis: true, labelKey: 'dashboard:customize.widgets.kpi.label', descriptionKey: 'dashboard:customize.widgets.kpi.description' },
  payments: { defaultW: 6, defaultH: 9, minW: COLS, maxW: COLS, minH: 4, requiresFullKpis: true, labelKey: 'dashboard:customerPayments.title', descriptionKey: 'dashboard:customize.widgets.payments.description' },
  finance: { defaultW: 6, defaultH: 3, minW: 3, maxW: COLS, minH: 3, requiresFullKpis: true, labelKey: 'dashboard:financial.title', descriptionKey: 'dashboard:customize.widgets.finance.description' },
  priorities: { defaultW: 4, defaultH: 4, minW: 2, maxW: COLS, minH: 3, requiresFullKpis: false, labelKey: 'dashboard:priorities.title', descriptionKey: 'dashboard:customize.widgets.priorities.description' },
  week: { defaultW: 2, defaultH: 4, minW: 2, maxW: COLS, minH: 3, requiresFullKpis: false, labelKey: 'dashboard:week.title', descriptionKey: 'dashboard:customize.widgets.week.description' },
  status: { defaultW: 6, defaultH: 3, minW: 2, maxW: COLS, minH: 3, requiresFullKpis: false, labelKey: 'dashboard:statusCards.title', descriptionKey: 'dashboard:customize.widgets.status.description' },
  risks: { defaultW: 2, defaultH: 7, minW: 2, maxW: COLS, minH: 4, requiresFullKpis: false, labelKey: 'dashboard:sidePanel.title', descriptionKey: 'dashboard:customize.widgets.risks.description' },
  recent: { defaultW: 6, defaultH: 6, minW: 3, maxW: COLS, minH: 4, requiresFullKpis: false, labelKey: 'dashboard:recentOrders.title', descriptionKey: 'dashboard:customize.widgets.recent.description' },
  workQueue: { defaultW: 4, defaultH: 7, minW: 2, maxW: COLS, minH: 3, requiresFullKpis: false, labelKey: 'dashboard:customize.widgets.workQueue.label', descriptionKey: 'dashboard:customize.widgets.workQueue.description' },
  upcoming: { defaultW: 3, defaultH: 5, minW: 2, maxW: COLS, minH: 3, requiresFullKpis: false, labelKey: 'dashboard:customize.widgets.upcoming.label', descriptionKey: 'dashboard:customize.widgets.upcoming.description' },
  statusChart: { defaultW: 2, defaultH: 4, minW: 2, maxW: COLS, minH: 3, requiresFullKpis: false, labelKey: 'dashboard:customize.widgets.statusChart.label', descriptionKey: 'dashboard:customize.widgets.statusChart.description' },
  quickActions: { defaultW: 2, defaultH: 3, minW: 2, maxW: COLS, minH: 2, requiresFullKpis: false, labelKey: 'dashboard:customize.widgets.quickActions.label', descriptionKey: 'dashboard:customize.widgets.quickActions.description' },
  upcomingTransports: { defaultW: 3, defaultH: 5, minW: 2, maxW: COLS, minH: 3, requiresFullKpis: false, requires: 'transports', labelKey: 'dashboard:customize.widgets.upcomingTransports.label', descriptionKey: 'dashboard:customize.widgets.upcomingTransports.description' },
  expectedDeliveries: { defaultW: 3, defaultH: 5, minW: 2, maxW: COLS, minH: 3, requiresFullKpis: false, requires: 'supplierOrders', labelKey: 'dashboard:customize.widgets.expectedDeliveries.label', descriptionKey: 'dashboard:customize.widgets.expectedDeliveries.description' },
  myTasks: { defaultW: 3, defaultH: 5, minW: 2, maxW: COLS, minH: 3, requiresFullKpis: false, requires: 'tasks', labelKey: 'dashboard:customize.widgets.myTasks.label', descriptionKey: 'dashboard:customize.widgets.myTasks.description' },
};

// The layout a user sees before they change anything; the rest are added from
// „Dodaj widżet”. What needs doing comes first (priorities, the week, the
// orders with their next step), money below. „Kluczowe liczby” repeats the
// priorities as a row of counts, so it is not on the panel by default
// (DESIGN.md §6 „KPI wall”).
const DEFAULT_WIDGET_IDS: DashboardWidgetId[] = ['priorities', 'week', 'workQueue', 'risks', 'status', 'payments', 'finance', 'recent'];

const DASHBOARD_WIDGET_DEFINITIONS: DashboardWidgetDefinition[] = DASHBOARD_WIDGET_IDS.map((id) => ({ id, ...DEFINITIONS[id] }));

export function getAvailableDashboardWidgets(access: DashboardAccess): DashboardWidgetDefinition[] {
  return DASHBOARD_WIDGET_DEFINITIONS.filter((widget) => (!widget.requiresFullKpis || access.fullKpis) && (!widget.requires || access[widget.requires]));
}

export function getDashboardWidgetDefinition(id: DashboardWidgetId): DashboardWidgetDefinition {
  return DASHBOARD_WIDGET_DEFINITIONS.find((widget) => widget.id === id)!;
}

// Places widgets left to right, top to bottom, in list order. A row is as tall
// as its tallest widget; a widget that does not fit the row starts a new one.
export function packDashboardLayout(
  items: Array<{ id: DashboardWidgetId; w: number; h: number }>,
): DashboardWidgetLayoutItem[] {
  const placed: DashboardWidgetLayoutItem[] = [];
  let x = 0;
  let y = 0;
  let rowHeight = 0;
  for (const item of items) {
    if (x + item.w > COLS) {
      x = 0;
      y += rowHeight;
      rowHeight = 0;
    }
    placed.push({ id: item.id, x, y, w: item.w, h: item.h });
    x += item.w;
    rowHeight = Math.max(rowHeight, item.h);
  }
  return placed;
}

export function getDefaultDashboardLayout(access: DashboardAccess): DashboardWidgetLayoutItem[] {
  const available = new Set(getAvailableDashboardWidgets(access).map((widget) => widget.id));
  return packDashboardLayout(
    DEFAULT_WIDGET_IDS
      .filter((id) => available.has(id))
      .map((id) => ({ id, w: getDashboardWidgetDefinition(id).defaultW, h: getDashboardWidgetDefinition(id).defaultH })),
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

// Tallest a saved widget may be (rows). Keeps a corrupt or hand-edited value
// from producing a tile far taller than the screen.
export const DASHBOARD_MAX_ROWS = 12;

// `stored` is the saved layout from dashboardLayoutStorage. null (never saved,
// or reset) means the default layout. Accepted shapes, newest first:
//  - { id, x, y, w, h } (grid positions, current)
// Ids the role may not see, unknown ids and duplicates are dropped; sizes are
// clamped to what each widget allows. An empty saved list stays empty, while a
// list that holds only widgets this role cannot see falls back to the default.
// Entries with broken positions are placed below the rest, not allowed to
// discard the whole layout.
export function resolveDashboardLayout(stored: unknown, access: DashboardAccess): DashboardWidgetLayoutItem[] {
  if (!Array.isArray(stored)) return getDefaultDashboardLayout(access);
  if (stored.length === 0) return [];

  const available = getAvailableDashboardWidgets(access);
  const seen = new Set<DashboardWidgetId>();
  const entries: Array<{ definition: DashboardWidgetDefinition; raw: Record<string, unknown> | null }> = [];
  for (const entry of stored) {
    const raw = typeof entry === 'string' ? null : (entry as Record<string, unknown> | null);
    const id = typeof entry === 'string' ? entry : raw?.id;
    const definition = available.find((widget) => widget.id === id);
    if (!definition || seen.has(definition.id)) continue;
    seen.add(definition.id);
    entries.push({ definition, raw });
  }
  if (entries.length === 0) return getDefaultDashboardLayout(access);

  const positioned: DashboardWidgetLayoutItem[] = [];
  const unpositioned: Array<{ id: DashboardWidgetId; w: number; h: number }> = [];
  for (const { definition, raw } of entries) {
    if (raw && isFiniteNumber(raw.x) && isFiniteNumber(raw.y) && isFiniteNumber(raw.w) && isFiniteNumber(raw.h)) {
      const w = clamp(Math.round(raw.w), definition.minW, definition.maxW);
      positioned.push({
        id: definition.id,
        x: clamp(Math.round(raw.x), 0, COLS - w),
        y: Math.max(0, Math.round(raw.y)),
        w,
        h: clamp(Math.round(raw.h), definition.minH, DASHBOARD_MAX_ROWS),
      });
    } else {
      unpositioned.push({ id: definition.id, w: definition.defaultW, h: definition.defaultH });
    }
  }

  if (positioned.length === 0) return packDashboardLayout(unpositioned);
  // Entries without positions go below the saved ones, one per row.
  let bottom = positioned.reduce((max, item) => Math.max(max, item.y + item.h), 0);
  const appended = unpositioned.map((item) => {
    const placed = { id: item.id, x: 0, y: bottom, w: item.w, h: item.h };
    bottom += item.h;
    return placed;
  });
  return [...positioned, ...appended];
}

export function isDefaultDashboardLayout(layout: DashboardWidgetLayoutItem[], access: DashboardAccess): boolean {
  const defaults = getDefaultDashboardLayout(access);
  return layout.length === defaults.length
    && layout.every((item, index) => {
      const expected = defaults[index];
      return item.id === expected.id && item.x === expected.x && item.y === expected.y && item.w === expected.w && item.h === expected.h;
    });
}
